import { useCallback, useEffect, useRef, useState } from 'react'

import { useSession } from 'next-auth/react'
import Image from 'next/image'
import { useRouter } from 'next/router'

import { lang } from '@lib/constants'
import useUpdateShopifyFavorites from '@lib/hooks/useUpdateShopifyFavorites'
import type { Image as ImageType } from '@lib/types'
import type { Product } from '@lib/types/product'

type Props = {
	id?: string
	productImages: Product['images']
	fullScreen?: boolean
	setModalOpen?: (status: boolean) => void
	setCurrentImageHandler: (image: ImageType) => void

	isFavorite?: boolean
	showFavorite?: boolean
	currentImage?: ImageType
}

export default function Images({
	id,
	productImages,
	fullScreen,
	setModalOpen,
	setCurrentImageHandler,
	isFavorite,
	currentImage,
	showFavorite = true
}: Props) {
	const imagesRef = useRef<HTMLDivElement>(null)
	const router = useRouter()
	const { data: session } = useSession()
	const updateShopifyFavorites = useUpdateShopifyFavorites()
	const [favoriteProduct, setFavoriteProduct] = useState(isFavorite)

	const escFunction = useCallback(
		(event: any) => {
			if (event.keyCode === 27) {
				setModalOpen?.(false)
			}
		},
		[setModalOpen]
	)

	useEffect(() => {
		document.addEventListener('keydown', escFunction)
		return () => {
			document.removeEventListener('keydown', escFunction)
		}
	}, [escFunction])

	// Scroll to an image back and forth
	const scroll = (offset: number) => {
		if (imagesRef.current) imagesRef.current.scrollLeft += offset
	}

	// Handle favorite selection (adding or removing)
	const handleFavorite = () => {
		if (session) {
			if (id) {
				const currentFavorite = favoriteProduct
				setFavoriteProduct(!favoriteProduct)
				updateShopifyFavorites(id, !currentFavorite ? 'add' : 'remove')
			}
		}
		// Ask customer to login if they are not logged in
		else router.push(lang === 'es-es' ? '/iniciar-sesion' : '/login')
	}

	// Reactively set favorite state
	useEffect(() => {
		setFavoriteProduct(isFavorite)
	}, [isFavorite])

	return (
		<div className="relative flex flex-col flex-1">
			{/* Top Image */}
			{!fullScreen && showFavorite && (
				// Favorite
				// A11Y: min-w/h-[44px] ensures WCAG 2.5.5 touch target size compliance
				<button
					type="button"
					className="absolute z-20 right-4 top-4 min-w-[44px] min-h-[44px] flex items-center justify-center text-xl"
					onClick={() => handleFavorite?.()}
					aria-label={favoriteProduct ? 'Remove from favorites' : 'Add to favorites'}
				>
					<i className={`fa-heart text-bbx-spark ${favoriteProduct ? 'fa-solid' : 'fa-regular'}`} aria-hidden="true" />
				</button>
			)}

			{/* Main image */}
			<div
				className={`relative flex items-center justify-center ${
					fullScreen ? 'flex-1 mb-4' : 'h-[450px] mb-12 cursor-zoom-in'
				}`}
			>
				{currentImage?.url && (
					<Image
						onClick={() => {
							setModalOpen?.(true)
						}}
						layout="fill"
						style={{ objectFit: 'contain' }}
						src={currentImage.url}
						alt={currentImage.altText || ''}
					/>
				)}
			</div>

			{/* Image thumbnails nav */}
			{productImages.length > 1 && (
				<nav aria-label="Product image thumbnails" className="flex items-center justify-center">
					{productImages.length > 5 && (
						// Scroll left
						<button
							onClick={() => scroll(-300)}
							type="button"
							className="pl-2 pr-8 max-md:hidden"
							aria-label="Previous"
						>
							<i className="text-2xl fa-thin fa-angle-left" />
						</button>
					)}

					{/* List of images */}
					<div className="image-thumb-scroller" ref={imagesRef}>
						{productImages.map((image) => (
							<button
								type="button"
								className={`flex flex-1 flex-shrink-0 w-16 h-16 md:w-24 md:h-24 p-2 border aspect-square snap-start', ${
									currentImage?.url === image.url ? 'border-gray-400' : 'border-transparent'
								}`}
								onClick={() => setCurrentImageHandler?.(image)}
								key={image.id}
							>
								<Image
									src={image.url}
									width={100}
									height={100}
									className="object-contain"
									alt={image.altText || ''}
								/>
							</button>
						))}
					</div>

					{productImages.length > 5 && (
						// Scroll right
						<button
							onClick={() => scroll(300)}
							type="button"
							className="pl-8 pr-2 max-md:hidden"
							aria-label="Next"
						>
							<i className="text-2xl fa-thin fa-angle-right" />
						</button>
					)}
				</nav>
			)}
		</div>
	)
}
